import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type TopicIdentityRecord } from "@/shared/domain/topics";
import { type PaginatedResponse } from "@/shared/http";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseSessionTopicsParams {
  sessionId: string;
  pageToken?: string;
}

/**
 * Fetches one page of topic identities contributing to a Session.
 *
 * Returns the `PaginatedResponse` unchanged so callers can drive a paginator
 * off `next_token`.
 */
export function useSessionTopics({
  sessionId,
  pageToken,
}: UseSessionTopicsParams): UseQueryResult<
  PaginatedResponse<TopicIdentityRecord>,
  Error
> {
  const { sessionService } = useDomainServices();
  return useQuery<PaginatedResponse<TopicIdentityRecord>, Error>({
    queryKey: sessionQueryKeys.topics(sessionId, pageToken),
    queryFn: ({ signal }) =>
      sessionService.listSessionTopics(sessionId, {
        abortSignal: signal,
        pageToken,
      }),
  });
}
